import { useBonusInstances } from '@/hooks/use-bonuses'
import { StatusBadge } from './StatusBadge'
import { formatPeriodLabel, type Timeframe, type BonusInstance } from '@/types'
import { calendarDateOf, formatCalendarDate } from '../../functions-src/lib/calendar'

interface PeriodHistoryListProps {
  configId: string
  onInstanceClick: (instanceId: string) => void
}

export function PeriodHistoryList({ configId, onInstanceClick }: PeriodHistoryListProps) {
  const { data, isLoading, error } = useBonusInstances(configId)

  if (isLoading) {
    return (
      <div className="p-4 space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-center text-danger-600">
        Failed to load periods
      </div>
    )
  }

  if (!data?.instances.length) {
    return (
      <div className="p-4 text-center text-gray-500">
        No period history yet
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Period</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Value</th>
            <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Dispatch</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.instances.map((instance) => (
            <PeriodRow
              key={instance.id}
              instance={instance}
              onClick={() => onInstanceClick(instance.id)}
            />
          ))}
        </tbody>
      </table>

      {data.total > data.instances.length && (
        <div className="p-3 text-center">
          <button className="text-sm text-brand-teal hover:underline">
            Load more ({data.total - data.instances.length} remaining)
          </button>
        </div>
      )}
    </div>
  )
}

interface PeriodRowProps {
  instance: BonusInstance & { goal_timeframe?: Timeframe }
  onClick: () => void
}

function PeriodRow({ instance, onClick }: PeriodRowProps) {
  const periodLabel = formatPeriodLabel(instance.period_start, instance.goal_timeframe)

  return (
    <tr
      onClick={onClick}
      className="hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <td className="px-4 py-3 text-sm text-gray-900">
        {periodLabel}
        <span className="text-xs text-gray-500 ml-2">
          ({formatCalendarDate(calendarDateOf(instance.period_start))} - {formatCalendarDate(calendarDateOf(instance.period_end))})
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
        {instance.metric_value_at_close?.toLocaleString() ?? '—'}
      </td>
      <td className="px-4 py-3 text-center">
        {instance.is_met ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
            Met
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
            Not Met
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <StatusBadge status={instance.dispatch_status} />
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className="text-brand-teal hover:text-brand-teal/80 text-sm"
        >
          View
        </button>
      </td>
    </tr>
  )
}
